import { useState, useRef, useEffect } from "react";

interface Message {
  id: number;
  from: "buyer" | "seller";
  text: string;
  time: string;
  read: boolean;
}

interface Props {
  productName: string;
  sellerName: string;
  sellerFlag: string;
  orderId?: string;
  buyerName: string;
  onClose: () => void;
}

const initialMessages: Message[] = [
  {
    id: 1,
    from: "seller",
    text: "Hello! Thank you for your order. Your package is being prepared at our warehouse. Please let me know if you have any questions!",
    time: "Apr 8, 10:30 AM",
    read: true,
  },
  {
    id: 2,
    from: "buyer",
    text: "Hi! When will my order be shipped? I need it by next week.",
    time: "Apr 8, 11:15 AM",
    read: true,
  },
  {
    id: 3,
    from: "seller",
    text: "Your order will be shipped within 24 hours. You will receive a UPS tracking number via email. Estimated delivery is 2–3 business days to your location.",
    time: "Apr 8, 11:45 AM",
    read: true,
  },
];

export default function MessageModal({ productName, sellerName, sellerFlag, orderId, buyerName, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState("");
  const [charCount, setCharCount] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    const newMsg: Message = {
      id: messages.length + 1,
      from: "buyer",
      text: input.trim(),
      time: new Date().toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
      read: false,
    };
    setMessages(prev => [...prev, newMsg]);
    setInput("");
    setCharCount(0);

    // Simulate seller auto-reply
    setTimeout(() => {
      const replies = [
        "Thank you for your message! I will get back to you shortly.",
        "Great question! Let me check on that for you right away.",
        "I understand your concern. Our team is looking into this.",
        "Your satisfaction is our priority. We will resolve this as soon as possible.",
        "Thank you for reaching out! Please allow 1–2 hours for a detailed response.",
      ];
      const reply: Message = {
        id: messages.length + 2,
        from: "seller",
        text: replies[Math.floor(Math.random() * replies.length)],
        time: new Date().toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
        read: false,
      };
      setMessages(prev => [...prev, reply]);
    }, 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md flex flex-col" style={{ height: "560px" }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-full text-xl shrink-0">
            {sellerFlag}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">{sellerName}</h3>
            <p className="text-xs text-gray-400 truncate">{productName}</p>
            {orderId && <p className="text-xs text-amber-500 font-semibold">{orderId}</p>}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 bg-green-400 rounded-full inline-block"></span>
            <span className="text-xs text-green-600 font-semibold">Online</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer ml-2">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.from === "buyer" ? "justify-end" : "justify-start"}`}>
              {msg.from === "seller" && (
                <div className="w-7 h-7 flex items-center justify-center bg-amber-50 rounded-full text-sm shrink-0 mr-2 mt-1">
                  {sellerFlag}
                </div>
              )}
              <div className={`max-w-[75%] ${msg.from === "buyer" ? "items-end" : "items-start"} flex flex-col gap-1`}>
                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.from === "buyer"
                    ? "bg-amber-400 text-white rounded-br-sm"
                    : "bg-gray-100 text-gray-800 rounded-bl-sm"
                }`}>
                  {msg.text}
                </div>
                <span className="text-xs text-gray-400 px-1">{msg.time}</span>
              </div>
              {msg.from === "buyer" && (
                <div className="w-7 h-7 flex items-center justify-center bg-stone-100 rounded-full text-xs font-bold text-stone-600 shrink-0 ml-2 mt-1">
                  {buyerName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef}></div>
        </div>

        {/* Input */}
        <div className="px-5 py-4 border-t border-gray-100 shrink-0">
          <form onSubmit={handleSend} className="flex gap-2">
            <div className="flex-1 relative">
              <input
                value={input}
                onChange={e => {
                  if (e.target.value.length <= 500) {
                    setInput(e.target.value);
                    setCharCount(e.target.value.length);
                  }
                }}
                placeholder="Type a message..."
                className="w-full border border-gray-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors pr-16"
              />
              {charCount > 400 && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{500 - charCount}</span>
              )}
            </div>
            <button
              type="submit"
              disabled={!input.trim()}
              className="w-10 h-10 flex items-center justify-center bg-amber-400 hover:bg-amber-500 disabled:bg-gray-200 text-white rounded-full transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-send-plane-fill text-sm"></i>
            </button>
          </form>
          <p className="text-xs text-gray-400 text-center mt-2">Messages are monitored for safety. Max 500 characters.</p>
        </div>
      </div>
    </div>
  );
}
